import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications,
  AccountCircle,
  Logout,
  Person,
  Settings,
  Language,
  Brightness4,
  Brightness7,
  DisplaySettings,
} from '@mui/icons-material';

import { useAuth, useRole } from '../../contexts/AuthContext';
import { getBreadcrumbs } from '../../utils/navigation';

interface NavbarProps {
  onMenuToggle: () => void;
  sidebarOpen: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ onMenuToggle, sidebarOpen }) => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { canManageUsers } = useRole();
  const navigate = useNavigate();
  const location = useLocation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [languageMenuEl, setLanguageMenuEl] = useState<null | HTMLElement>(null);

  const isMenuOpen = Boolean(anchorEl);
  const isLanguageMenuOpen = Boolean(languageMenuEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/login');
  };

  const handleProfile = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleMenuClose();
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    handleLanguageMenuClose();
  };

  const getRoleDisplayName = (role: string): string => {
    return t(`users.userRoles.${role}`);
  };

  const getRoleColor = (role: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'primary';
      case 'scheduler': return 'info';
      case 'engineer': return 'warning';
      case 'director': return 'secondary';
      default: return 'success';
    }
  };

  // Получаем breadcrumbs для текущего пути
  const breadcrumbs = getBreadcrumbs(location.pathname);

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      slotProps={{
        paper: {
          elevation: 8,
          sx: {
            minWidth: 250,
            mt: 1,
          },
        },
      }}
    >
      {/* Информация о пользователе */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {user?.full_name || user?.username}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.email}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Chip
            label={getRoleDisplayName(user?.role || 'viewer')}
            color={getRoleColor(user?.role || 'viewer')}
            size="small"
          />
        </Box>
      </Box>

      <MenuItem onClick={handleProfile}>
        <ListItemIcon>
          <Person />
        </ListItemIcon>
        <ListItemText>{t('auth.profile')}</ListItemText>
      </MenuItem>

      {canManageUsers() && (
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <Settings />
          </ListItemIcon>
          <ListItemText>{t('navigation.settings')}</ListItemText>
        </MenuItem>
      )}

      <Divider />

      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <Logout />
        </ListItemIcon>
        <ListItemText>{t('auth.logout')}</ListItemText>
      </MenuItem>
    </Menu>
  );

  const renderLanguageMenu = (
    <Menu
      anchorEl={languageMenuEl}
      open={isLanguageMenuOpen}
      onClose={handleLanguageMenuClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <MenuItem
        onClick={() => handleLanguageChange('ru')}
        selected={i18n.language === 'ru'}
      >
        🇷🇺 Русский
      </MenuItem>
      <MenuItem
        onClick={() => handleLanguageChange('en')}
        selected={i18n.language === 'en'}
      >
        🇺🇸 English
      </MenuItem>
    </Menu>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          {/* Кнопка меню */}
          <IconButton
            color="inherit"
            aria-label="toggle sidebar"
            onClick={onMenuToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Логотип и название */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <DisplaySettings sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
              {t('app.title')}
            </Typography>
          </Box>

          {/* Breadcrumbs */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            {breadcrumbs.map((crumb, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                {index > 0 && (
                  <Typography sx={{ mx: 1, color: 'text.secondary' }}>/</Typography>
                )}
                {crumb.path ? (
                  <Button
                    onClick={() => navigate(crumb.path!)}
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                  >
                    {t(crumb.label)}
                  </Button>
                ) : (
                  <Typography color="text.primary" fontWeight="medium">
                    {t(crumb.label)}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          {/* Действия справа */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Переключатель языка */}
            <Tooltip title={t('common.language')}>
              <IconButton
                color="inherit"
                onClick={handleLanguageMenuOpen}
              >
                <Language />
              </IconButton>
            </Tooltip>

            {/* Уведомления */}
            <Tooltip title={t('notifications.title', { defaultValue: 'Уведомления' })}>
              <IconButton color="inherit">
                <Badge badgeContent={0} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Профиль пользователя */}
            <Tooltip title={user?.full_name || user?.username || t('auth.profile')}>
              <IconButton
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{ ml: 1 }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  {(user?.full_name || user?.username || 'U').charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {renderMenu}
      {renderLanguageMenu}
    </>
  );
};

export default Navbar; 
 
 
 